import {Component, Inject, Input} from '@angular/core';
import {UntypedFormArray} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, combineLatest, firstValueFrom, forkJoin} from 'rxjs';
import {filter, first, map, switchMap, takeUntil} from 'rxjs/operators';

import {BaseCtrlValueAccessorFormArrayComponent, FormManagerService} from '@sim/form';
import {ClassName} from '@sim/role';

import {filePath, SourceArcApiService, SwalService} from '@atmi/core';
import {SourceArc, SourceArcCard, SourceArcMeta} from '@atmi/core/models';
import {
  SourceArcDialogComponent,
  SourceArcDialogData
} from '@atmi/shared/source-arc-dialog/source-arc-dialog.component';

@Component({
  selector: 'app-source-arc-manager',
  styleUrls: ['./source-arc-manager.component.scss'],
  templateUrl: './source-arc-manager.component.html',
})
export class SourceArcManagerComponent extends BaseCtrlValueAccessorFormArrayComponent<SourceArc> {

  @Input() classNames: ClassName[] = [];

  private readonly cardId$ = new BehaviorSubject<string | undefined>(undefined);

  @Input() set cardId(v: string | undefined) {
    this.cardId$.next(v);
  }

  get cardId() {
    return this.cardId$.value;
  }

  readonly cardSourceArcs$ = combineLatest([
    this.cardId$.pipe(),
    this.formValue$.pipe(filter((form): form is UntypedFormArray => !!form)),
  ]).pipe(
    switchMap(([_, form]) => {
      const sourceArcsIds: string[] = form.value.map((s: SourceArc) => s.docArc);
      const sourceArcsPromises = sourceArcsIds.map((id) => firstValueFrom(this.sourceArcApi.get(id).pipe(first())));

      return sourceArcsPromises.length > 0
        ? forkJoin(sourceArcsPromises)
        : Promise.resolve([]);
    }),
    map((sourceArchival) => {
      if (!this.form) {
        throw new Error('Cannot set archival list without a form');
      }

      const sourcesArcs: SourceArc[] = this.form.value;
      return sourcesArcs.map((source) => {
        const linked = sourceArchival.find((s) => s.id === source.docArc);
        return {
          ...source,
          docArc: {
            ...linked,
            previewUrl: linked?.previewUrl ? this.fileUrl + linked.previewUrl : undefined,
          },
          ordine: source.ordine,
        };
      });
    }),
    takeUntil(this.unsubscription$),
  );

  constructor(
    @Inject(filePath) private readonly fileUrl: string,
    private readonly dialog: MatDialog,
    private readonly formManager: FormManagerService,
    private readonly sourceArcApi: SourceArcApiService,
    private readonly swal: SwalService,
    private readonly translate: TranslateService,
  ) {
    super();
  }

  orderSourceArcHandler(items: Array<SourceArcCard & { docArc: { id: string } }>) {
    this.value = items.map((i, index) => ({
      docArc: i.docArc.id,
      ordine: index + 1,
    }));
  }

  async addSourceArcHandler(lastSourceArc?: SourceArcCard) {
    // @TODO passare il cardtype per ora di default oa
    const data: SourceArcDialogData = {
      order: lastSourceArc ? lastSourceArc.ordine : 1,
      objectId: this.cardId,
      objectType: 'oa'
    };

    const ref = this.dialog.open(SourceArcDialogComponent, {
      data,
      width: '70vw',
      height: '80vh'
    });
    const res: SourceArc | undefined = await firstValueFrom(ref.afterClosed().pipe(first()));

    if (res && this.form) {
      const group = this.formManager.makeForm(SourceArcMeta, res);

      this.form.push(group);
      this.value = [...this.form.value];
    }
  }

  // tslint:disable-next-line:variable-name
  async removeSourceArcHandler(_: SourceArcCard, index: number) {
    const res = await this.swal.showConfirm({
      text: this.translate.instant('Sei sicuro di voler rimuovere questa fonte?'),
      title: this.translate.instant('Rimozione fonte archivistica'),
    });

    if (res && this.form) {
      this.form.removeAt(index);
      this.value = [...this.form.value];
    }
  }
}
